import { ThemeEvents } from '@theme/events';
import { addVariantToBag, notifyPurchase } from './product-form.js';
import {
  PURCHASE_MESSAGES,
  normalizePurchaseMessage,
} from './velzeen-purchase-ui.js';

const PURCHASE_ROOT = '.buy-buttons-block[data-pdp-purchase]';
const FORM_SELECTOR = 'form.vz-native-product-form';
const SUBMIT_SELECTOR = '[data-native-purchase-action="add"]';
const COMMERCE_CONTROLLER_SCOPE = 'commerce_controller';
const COMMERCE_CONTROLLER_READY_REASON = 'native_product_form_submit_handler_registered';
const COMMERCE_CONTROLLER_READY_EVENT = 'velzeen:commerce-controller-ready';

const number = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

function signalCommerceControllerReady() {
  const guard = window.VelzeenWebview = window.VelzeenWebview || {};
  const details = {
    script: 'velzeen-native-product-form.js',
    controller: 'native_product_form',
    formSelector: FORM_SELECTOR,
  };

  guard.nativeProductFormReady = details;

  if (typeof guard.signalCommerceControllerReady === 'function') {
    guard.signalCommerceControllerReady(details);
  } else {
    guard.pendingSignals = Array.isArray(guard.pendingSignals) ? guard.pendingSignals : [];

    const alreadyQueued = guard.pendingSignals.some(
      (signal) =>
        signal?.status === 'ready' &&
        signal?.scope === COMMERCE_CONTROLLER_SCOPE &&
        signal?.reason === COMMERCE_CONTROLLER_READY_REASON
    );

    if (!alreadyQueued) {
      guard.pendingSignals.push({
        status: 'ready',
        scope: COMMERCE_CONTROLLER_SCOPE,
        reason: COMMERCE_CONTROLLER_READY_REASON,
        details,
      });
    }
  }

  try {
    window.dispatchEvent(new CustomEvent(COMMERCE_CONTROLLER_READY_EVENT, { detail: details }));
  } catch {
    try {
      window.dispatchEvent(new Event(COMMERCE_CONTROLLER_READY_EVENT));
    } catch {
      // Readiness is already available through the guard API or pending queue.
    }
  }
}

function enhancementAvailable() {
  if (typeof window.fetch !== 'function' || typeof window.Promise !== 'function') return false;

  const capabilities = window.VelzeenWebview?.capabilities;
  return !capabilities || capabilities.enhanced !== false;
}

function quantityInput(root) {
  return root.querySelector('[data-quantity-input]');
}

function selectedQuantity(root) {
  return Math.max(1, Math.floor(number(quantityInput(root)?.value, 1)));
}

function variantInput(root) {
  return root.querySelector('[data-variant-input]');
}

function primaryButton(root) {
  return root.querySelector(SUBMIT_SELECTOR);
}

function setBusy(root, busy) {
  const button = primaryButton(root);
  const disabled = busy || root.dataset.variantAvailable !== 'true';
  if (button instanceof HTMLButtonElement) {
    button.disabled = disabled;
    button.setAttribute('aria-disabled', String(disabled));
    if (disabled) button.setAttribute('tabindex', '-1');
    else button.removeAttribute('tabindex');
  }

  if (busy) root.setAttribute('aria-busy', 'true');
  else root.removeAttribute('aria-busy');
}

function resetQuantity(root) {
  const input = quantityInput(root);
  if (!(input instanceof HTMLInputElement)) return;

  input.value = '1';
  input.setAttribute('aria-valuenow', '1');
  input.dispatchEvent(new Event('change', { bubbles: true }));
}

function synchronizeNativeControls(root, source) {
  for (const name of [
    'productId',
    'variantId',
    'variantAvailable',
    'inventoryTracked',
    'inventoryQuantity',
  ]) {
    if (source.dataset[name] === undefined) delete root.dataset[name];
    else root.dataset[name] = source.dataset[name];
  }

  const currentVariantInput = variantInput(root);
  const sourceVariantInput = variantInput(source);
  if (currentVariantInput instanceof HTMLInputElement && sourceVariantInput instanceof HTMLInputElement) {
    currentVariantInput.value = sourceVariantInput.value;
  } else if (currentVariantInput instanceof HTMLInputElement && root.dataset.variantId) {
    currentVariantInput.value = root.dataset.variantId;
  }

  const currentButton = primaryButton(root);
  const sourceButton = primaryButton(source);
  if (!(currentButton instanceof HTMLButtonElement) || !(sourceButton instanceof HTMLButtonElement)) return;

  currentButton.innerHTML = sourceButton.innerHTML;
  currentButton.disabled = sourceButton.disabled;
  currentButton.setAttribute(
    'aria-disabled',
    sourceButton.getAttribute('aria-disabled') || String(sourceButton.disabled)
  );

  if (sourceButton.hasAttribute('tabindex')) {
    currentButton.setAttribute('tabindex', sourceButton.getAttribute('tabindex') || '-1');
  } else {
    currentButton.removeAttribute('tabindex');
  }
}

window.addEventListener(
  'submit',
  async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches(FORM_SELECTOR)) return;

    const root = form.closest(PURCHASE_ROOT);
    if (!(root instanceof HTMLElement)) return;

    const submitter = event.submitter;
    if (submitter instanceof Element && !submitter.matches(SUBMIT_SELECTOR)) return;

    // Do not take ownership unless every progressive-enhancement dependency is
    // available. In limited WebViews or controller failures, the browser keeps
    // the native Shopify form submission path.
    if (!enhancementAvailable()) return;

    const selectedVariant = number(variantInput(root)?.value || root.dataset.variantId);
    if (!selectedVariant || root.dataset.variantAvailable !== 'true') {
      event.preventDefault();
      notifyPurchase(root, PURCHASE_MESSAGES.unavailable);
      return;
    }

    event.preventDefault();
    setBusy(root, true);

    try {
      await addVariantToBag({
        variantId: selectedVariant,
        quantity: selectedQuantity(root),
        inventoryTracked: root.dataset.inventoryTracked === 'true',
        inventory: Math.max(0, number(root.dataset.inventoryQuantity)),
        source: root,
      });

      resetQuantity(root);
      notifyPurchase(root, PURCHASE_MESSAGES.added);
    } catch (error) {
      notifyPurchase(
        root,
        normalizePurchaseMessage(
          error instanceof Error ? error.message : '',
          PURCHASE_MESSAGES.addError
        )
      );
    } finally {
      setBusy(root, false);
    }
  },
  true
);

signalCommerceControllerReady();

document.addEventListener(ThemeEvents.variantUpdate, (event) => {
  const html = event.detail?.data?.html;
  if (!(event.target instanceof Element) || !(html instanceof Document)) return;

  const scope =
    event.target.closest('#quick-add-modal-content') ||
    event.target.closest('.shopify-section') ||
    document;

  for (const root of scope.querySelectorAll(PURCHASE_ROOT)) {
    if (!(root instanceof HTMLElement)) continue;

    const blockId = root.dataset.purchaseBlockId;
    const source = Array.from(html.querySelectorAll(PURCHASE_ROOT)).find(
      (item) => !blockId || item.dataset.purchaseBlockId === blockId
    );
    if (!(source instanceof HTMLElement)) continue;

    synchronizeNativeControls(root, source);
  }
});